import React, { FC, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { DocumentNode } from "graphql";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  Select,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { BusinessRevenueAnalytics, AnalyticsPeriodFilter } from "../types";
import { PERIOD_OPTIONS, PeriodOption } from "../constants";
import { formatNairaAbbreviated } from "../format";
import { formatAsNaira } from "../../../helpers/helpers";
import { RevenueChart } from "./RevenueChart";

interface RevenueDetailPageProps {
  title: string;
  description: string;
  query: DocumentNode;
  queryRootField:
    | "adminMaintenanceRevenue"
    | "adminTalentUnlockRevenue";
}

type QueryData = {
  [key: string]: BusinessRevenueAnalytics;
};

export const RevenueDetailPage: FC<RevenueDetailPageProps> = ({
  title,
  description,
  query,
  queryRootField,
}) => {
  const [period, setPeriod] = useState<AnalyticsPeriodFilter>(null);

  const { data, loading, error, refetch } = useQuery<QueryData>(query, {
    variables: { period },
  });

  const selectedOption = useMemo<PeriodOption>(() => {
    return (
      PERIOD_OPTIONS.find(option => option.value === period) ?? PERIOD_OPTIONS[0]
    );
  }, [period]);

  const analytics = data?.[queryRootField];

  return (
    <Box p={{ base: 4, md: 8 }} maxW="1100px" mx="auto">
      <Stack spacing={6} mb={8}>
        <Button
          as={RouterLink}
          to="/analytics"
          alignSelf="flex-start"
          size="sm"
          variant="link"
          color="primary.300"
          _hover={{ color: "brand.500", textDecoration: "none" }}
        >
          ← Business analytics
        </Button>
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "flex-end" }}
          gap={4}
        >
          <Box maxW="640px">
            <Heading size="xl" color="primary.500" letterSpacing="-0.02em">
              {title}
            </Heading>
            <Text color="primary.300" mt={2} fontSize="md">
              {description}
            </Text>
          </Box>
          <Box>
            <Text
              fontSize="xs"
              textTransform="uppercase"
              letterSpacing="wider"
              color="primary.300"
              mb={1}
            >
              Period
            </Text>
            <Select
              value={period ?? ""}
              onChange={event => {
                const next = event.target.value;
                setPeriod(next === "" ? null : (next as AnalyticsPeriodFilter));
              }}
              minWidth="200px"
              bg="white"
              borderColor="primary.150"
              data-testid="period-select"
            >
              {PERIOD_OPTIONS.map(option => (
                <option key={option.label} value={option.value ?? ""}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Box>
        </Flex>
      </Stack>

      {error && (
        <Alert status="error" mb={6} borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <AlertDescription>
              Couldn&apos;t load revenue analytics. Please try again.
            </AlertDescription>
          </Box>
          <Button size="sm" onClick={() => refetch()} colorScheme="red" variant="outline">
            Retry
          </Button>
        </Alert>
      )}

      <Stack spacing={8}>
        <Box>
          <Flex align="baseline" justify="space-between" mb={3}>
            <Text
              fontSize="xs"
              textTransform="uppercase"
              letterSpacing="wider"
              color="primary.300"
              fontWeight="semibold"
            >
              Period total
            </Text>
            <Text fontSize="xs" color="primary.300">
              Filtered by period · {selectedOption.totalLabelSuffix}
            </Text>
          </Flex>
          <Box
            bgGradient="linear(to-br, primary.500, #00263A)"
            color="white"
            borderRadius="xl"
            p={{ base: 6, md: 8 }}
            boxShadow="md"
            overflow="hidden"
            position="relative"
          >
            <Flex justify="space-between" align="flex-start" gap={4}>
              <Box>
                <Text
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  color="whiteAlpha.700"
                >
                  Total revenue
                </Text>
                {loading && !analytics ? (
                  <Skeleton
                    mt={3}
                    height="56px"
                    width="260px"
                    startColor="whiteAlpha.300"
                    endColor="whiteAlpha.500"
                  />
                ) : (
                  <Tooltip
                    label={formatAsNaira(analytics?.total ?? 0)}
                    hasArrow
                    bg="white"
                    color="primary.500"
                    fontWeight="medium"
                    px={3}
                    py={2}
                    borderRadius="md"
                    boxShadow="md"
                  >
                    <Heading
                      mt={2}
                      fontSize={{ base: "4xl", md: "5xl" }}
                      fontWeight="bold"
                      letterSpacing="-0.03em"
                      color="white"
                    >
                      {formatNairaAbbreviated(analytics?.total ?? 0)}
                    </Heading>
                  </Tooltip>
                )}
              </Box>
              <Box
                bg="whiteAlpha.200"
                borderRadius="full"
                px={3}
                py={1}
                fontSize="xs"
                fontWeight="medium"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                {selectedOption.totalLabelSuffix}
              </Box>
            </Flex>
            <Text mt={3} color="whiteAlpha.700" fontSize="sm">
              Hover the figure to see the exact amount.
            </Text>
          </Box>
        </Box>

        <Box position="relative">
          <Box
            position="absolute"
            top="-16px"
            left={0}
            right={0}
            borderTopWidth="1px"
            borderColor="primary.150"
          />
          <Flex align="baseline" justify="space-between" mb={3}>
            <Text
              fontSize="xs"
              textTransform="uppercase"
              letterSpacing="wider"
              color="primary.300"
              fontWeight="semibold"
            >
              Trend · last 6 months
            </Text>
            <Text fontSize="xs" color="primary.300">
              Always last 6 months · ignores period filter
            </Text>
          </Flex>
          <Box
            bg="white"
            borderRadius="xl"
            borderWidth="1px"
            borderColor="primary.150"
            p={{ base: 4, md: 6 }}
            boxShadow="sm"
          >
            <Heading size="sm" color="primary.500" mb={4} letterSpacing="-0.01em">
              Last 6 months
            </Heading>
            {loading && !analytics ? (
              <Skeleton height="340px" borderRadius="md" />
            ) : (
              <RevenueChart data={analytics?.monthlyTotals ?? []} />
            )}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};
